import { create } from 'zustand'
import { combine } from 'zustand/middleware'

import type { ExcelImportUsersDTOType, ImportUsersDTOType } from '@/features/admin/users/types'
import type { MenteeMatchingResponseType } from '@/features/matching/types'
import type { V2SessionType } from '@/features/sessions/types'
import {
  setModalStepAction,
  toggleModalAction,
  toggleShouldHideMatchingModalsAction,
  updateModalAttachedDatAction,
} from '@/features/ui/store/modal/modal.actions'
import subscribeToStore from '@/lib/zustand'

// common modal data type
type CommonModalType<T, D, S extends string[]> = {
  name: T
  activeStep: S[number]
  steps: S
  isOpen: boolean
  isSidebar?: boolean
  attachedData: Partial<D>
}

// phone modal type
export type PhoneModalType = CommonModalType<'phone', object, ['ask-phone']>

const PhoneModalItem = {
  name: 'phone',
  activeStep: 'ask-phone',
  steps: ['ask-phone'],
  isOpen: false,
  attachedData: {},
} satisfies PhoneModalType

// mentor matching modal
export type MentorMatchingModalType = CommonModalType<'mentor-matching', object, ['fill-criteria']>

const MentorMatchingModalItem = {
  name: 'mentor-matching',
  activeStep: 'fill-criteria',
  steps: ['fill-criteria'],
  isOpen: false,
  attachedData: {},
} satisfies MentorMatchingModalType

// mentee matching modal
export type MenteeMatchingModalType = CommonModalType<
  'mentee-matching',
  {
    mentors: MenteeMatchingResponseType['mentors']
    attemptNumber?: number
    sliderMountTime?: number
  },
  ['fill-criteria', 'matching-loading', 'matching-confirmation']
>

const MenteeMatchingModalItem = {
  name: 'mentee-matching',
  activeStep: 'fill-criteria',
  steps: ['fill-criteria', 'matching-loading', 'matching-confirmation'],
  isOpen: false,
  attachedData: {
    mentors: [],
  },
} satisfies MenteeMatchingModalType

// no program modal
export type NoProgramModalType = CommonModalType<'no-program', object, ['no-program-available']>

const NoProgramModalItem = {
  name: 'no-program',
  activeStep: 'no-program-available',
  steps: ['no-program-available'],
  isOpen: false,
  attachedData: {},
} satisfies NoProgramModalType

// with confirmation modal
export type ConfirmActionModalType = CommonModalType<
  'confirm-action',
  {
    fn: () => void | Promise<void>
    cancelFn?: () => void
    title: string
    /** 'danger' (défaut) : bouton rouge. 'default' : action non destructive, bouton principal. */
    type?: 'danger' | 'default'
    information?: string
    content?: string
    btnText?: string
  },
  ['confirm-action']
>

const ConfirmActionModalItem = {
  name: 'confirm-action',
  activeStep: 'confirm-action',
  steps: ['confirm-action'],
  isOpen: false,
  attachedData: {},
} satisfies ConfirmActionModalType

// new session modal
export type NewSessionModalType = CommonModalType<'new-session', object, ['new-session']>

const newSessionModalItem = {
  name: 'new-session',
  activeStep: 'new-session',
  steps: ['new-session'],
  isOpen: false,
  attachedData: {},
} satisfies NewSessionModalType

// meeting modal
export type MeetingModalType = CommonModalType<
  'meeting',
  {
    isResized: boolean
  },
  ['meeting']
>
const meetingModalItem = {
  name: 'meeting',
  activeStep: 'meeting',
  steps: ['meeting'],
  isOpen: false,
  attachedData: {
    isResized: false,
  },
} satisfies MeetingModalType

// user profile modal
export type UserProfileModalType = CommonModalType<
  'user-profile',
  {
    userId: string
  },
  ['user-profile']
>

const userProfileModalItem = {
  name: 'user-profile',
  activeStep: 'user-profile',
  steps: ['user-profile'],
  isSidebar: true,
  isOpen: false,
  attachedData: {},
} satisfies UserProfileModalType

// edit profile modal
export type EditProfileModalType = CommonModalType<'edit-profile', { userId: string; email?: string }, ['edit-profile']>

const editProfileModalItem = {
  name: 'edit-profile',
  activeStep: 'edit-profile',
  steps: ['edit-profile'],
  isOpen: false,
  attachedData: {},
} satisfies EditProfileModalType

// contact warning modal type
type ContactWarningModalStepsType = ['contact-warning']
export type ContactWarningModalType = CommonModalType<'contact-warning', object, ContactWarningModalStepsType>

const contactWarningModalItem = {
  name: 'contact-warning',
  activeStep: 'contact-warning',
  steps: ['contact-warning'],
  isOpen: false,
  attachedData: {},
} satisfies ContactWarningModalType

// profile description modal
export type ProfileDescriptionModalType = CommonModalType<
  'profile-description',
  { description?: string },
  ['description']
>

const profileDescriptionModalItem = {
  name: 'profile-description',
  activeStep: 'description',
  steps: ['description'],
  isOpen: false,
  attachedData: {},
} satisfies ProfileDescriptionModalType

// profile picture modal
export type ProfilePictureModalType = CommonModalType<'profile-picture', { profilePicture?: File }, ['profile-picture']>

const profilePictureModalItem = {
  name: 'profile-picture',
  activeStep: 'profile-picture',
  steps: ['profile-picture'],
  isOpen: false,
  attachedData: {},
} satisfies ProfilePictureModalType

// review session modal
export type ReviewSessionModalType = CommonModalType<
  'review-session',
  {
    sessionId: string
    rating: number
    review: string
  },
  ['rating', 'review', 'deliverable', 'presence', 'deliverables-rating', 'report', 'finished']
>

const reviewSessionModalItem = {
  name: 'review-session',
  activeStep: 'rating',
  steps: ['rating', 'review', 'deliverable', 'presence', 'deliverables-rating', 'report', 'finished'],
  isOpen: false,
  attachedData: {},
} satisfies ReviewSessionModalType

// session details modal
export type SessionDetailsModalType = CommonModalType<
  'session-details',
  {
    sessionId: string
  },
  ['session-details']
>

const sessionDetailsModalItem = {
  name: 'session-details',
  activeStep: 'session-details',
  steps: ['session-details'],
  isSidebar: true,
  isOpen: false,
  attachedData: {},
} satisfies SessionDetailsModalType

// admin users import
export type AdminUsersImportModalType = CommonModalType<
  'admin-users-import',
  { users: ExcelImportUsersDTOType['users'] | null },
  ['users-import', 'users-import-confirmation']
>

const adminUsersImportModalItem = {
  name: 'admin-users-import',
  activeStep: 'users-import',
  steps: ['users-import', 'users-import-confirmation'],
  isOpen: false,
  attachedData: { users: null },
} satisfies AdminUsersImportModalType

// admin manual user import
export type AdminManualUserImportModalType = CommonModalType<
  'admin-manual-user-import',
  { users: ImportUsersDTOType['users'] | null },
  ['manual-user-import']
>

const adminManualUserImportModalItem = {
  name: 'admin-manual-user-import',
  activeStep: 'manual-user-import',
  steps: ['manual-user-import'],
  isOpen: false,
  attachedData: { users: null },
} satisfies AdminManualUserImportModalType

export type NewMatchingModalType = CommonModalType<
  'new-matching',
  {
    mentors: MenteeMatchingResponseType['mentors']
  },
  ['new-matching-info', 'new-matching-fill-criteria', 'new-matching-loading', 'new-matching-confirmation']
>

const newMatchingModalItem = {
  name: 'new-matching',
  activeStep: 'new-matching-info',
  steps: ['new-matching-info', 'new-matching-fill-criteria', 'new-matching-loading', 'new-matching-confirmation'],
  isOpen: false,
  attachedData: { mentors: [] },
} satisfies NewMatchingModalType

export type AddToCalendarModalType = CommonModalType<
  'add-to-calendar',
  {
    session: V2SessionType
  },
  ['add-to-calendar']
>

const addToCalendarModalItem = {
  name: 'add-to-calendar',
  activeStep: 'add-to-calendar',
  steps: ['add-to-calendar'],
  isOpen: false,
  attachedData: {},
} satisfies AddToCalendarModalType
export type TermsModalType = CommonModalType<'terms', object, ['terms']>

const termsModalItem = {
  name: 'terms',
  activeStep: 'terms',
  steps: ['terms'],
  isOpen: false,
  attachedData: {},
} satisfies TermsModalType

export type AdminGroupStructureModalType = CommonModalType<
  'admin-group-structure',
  { groupId: string },
  ['group-structure']
>

const adminGroupStructureModalItem = {
  name: 'admin-group-structure',
  activeStep: 'group-structure',
  steps: ['group-structure'],
  isOpen: false,
  attachedData: {},
} satisfies AdminGroupStructureModalType

export type AdminReminderStructureModalType = CommonModalType<
  'admin-reminder-structure',
  { reminderId: string },
  ['reminder-structure']
>

const adminReminderStructureModalItem = {
  name: 'admin-reminder-structure',
  activeStep: 'reminder-structure',
  steps: ['reminder-structure'],
  isOpen: false,
  attachedData: {},
} satisfies AdminReminderStructureModalType

export type ProgramCriteriasModalType = CommonModalType<
  'program-criterias',
  { programId?: string; role?: 'mentor' | 'mentee' },
  ['program-criterias']
>

const programCriteriasModalItem = {
  name: 'program-criterias',
  activeStep: 'program-criterias',
  steps: ['program-criterias'],
  isOpen: false,
  attachedData: {},
} satisfies ProgramCriteriasModalType

export type GroupSettingsModalType = CommonModalType<'group-settings', { groupId: string }, ['group-settings']>

const groupSettingsModalItem = {
  name: 'group-settings',
  activeStep: 'group-settings',
  steps: ['group-settings'],
  isOpen: false,
  attachedData: {},
} satisfies GroupSettingsModalType

export type ObjectiveDescriptionModalType = CommonModalType<
  'objective-description',
  {
    title: string
    description: string
    order: number
  },
  ['objective-description']
>

const objectiveDescriptionModalItem = {
  name: 'objective-description',
  activeStep: 'objective-description',
  steps: ['objective-description'],
  isOpen: false,
  attachedData: {},
} satisfies ObjectiveDescriptionModalType

export type LinkSessionToObjectiveModalType = CommonModalType<
  'link-session-to-objective',
  {
    objectiveId: string
    groupId: string
  },
  ['link-session-to-objective']
>

const linkSessionToObjectiveModalItem = {
  name: 'link-session-to-objective',
  activeStep: 'link-session-to-objective',
  steps: ['link-session-to-objective'],
  isOpen: false,
  attachedData: {},
} satisfies LinkSessionToObjectiveModalType

export type EditSessionModalType = CommonModalType<
  'edit-session',
  {
    sessionId: string | null
  },
  ['edit-session']
>

const editSessionModalItem = {
  name: 'edit-session',
  activeStep: 'edit-session',
  steps: ['edit-session'],
  isOpen: false,
  attachedData: {},
} satisfies EditSessionModalType

export type NoMentorFoundModalType = CommonModalType<'no-mentor-found', object, ['no-mentor-found']>

const noMentorFoundModalItem = {
  name: 'no-mentor-found',
  activeStep: 'no-mentor-found',
  steps: ['no-mentor-found'],
  isOpen: false,
  attachedData: {},
} satisfies NoMentorFoundModalType

export type NameMissingModalType = CommonModalType<'name-missing', object, ['name-missing']>

const nameMissingModalItem = {
  name: 'name-missing',
  activeStep: 'name-missing',
  steps: ['name-missing'],
  isOpen: false,
  attachedData: {},
} satisfies NameMissingModalType

// MFA setup modal
export type MfaSetupModalType = CommonModalType<
  'mfa-setup',
  {
    qrCode: string
    secret: string
    backupCodes: string[]
  },
  ['mfa-setup']
>

const mfaSetupModalItem = {
  name: 'mfa-setup',
  activeStep: 'mfa-setup',
  steps: ['mfa-setup'],
  isOpen: false,
  attachedData: {},
} satisfies MfaSetupModalType

// MFA disable modal
export type MfaDisableModalType = CommonModalType<'mfa-disable', object, ['mfa-disable']>

const mfaDisableModalItem = {
  name: 'mfa-disable',
  activeStep: 'mfa-disable',
  steps: ['mfa-disable'],
  isOpen: false,
  attachedData: {},
} satisfies MfaDisableModalType

// MFA login modal
export type MfaLoginModalType = CommonModalType<'mfa-login', { email: string }, ['mfa-login']>

const mfaLoginModalItem = {
  name: 'mfa-login',
  activeStep: 'mfa-login',
  steps: ['mfa-login'],
  isOpen: false,
  attachedData: {},
} satisfies MfaLoginModalType

// program objective modal
export type ProgramObjectiveModalType = CommonModalType<'program-objective', object, ['program-objective']>

const programObjectiveModalItem = {
  name: 'program-objective',
  activeStep: 'program-objective',
  steps: ['program-objective'],
  isOpen: false,
  attachedData: {},
} satisfies ProgramObjectiveModalType

// Impersonation reason modal
export type ImpersonationReasonModalType = CommonModalType<
  'impersonation-reason',
  {
    targetUserId: string
    targetUserEmail: string
    targetUserRoles: string[]
  },
  ['impersonation-reason']
>

const impersonationReasonModalItem = {
  name: 'impersonation-reason',
  activeStep: 'impersonation-reason',
  steps: ['impersonation-reason'],
  isOpen: false,
  attachedData: {},
} satisfies ImpersonationReasonModalType

export type ModalStoreType =
  | PhoneModalType
  | MentorMatchingModalType
  | MenteeMatchingModalType
  | NoProgramModalType
  | ConfirmActionModalType
  | NewSessionModalType
  | MeetingModalType
  | UserProfileModalType
  | ProfileDescriptionModalType
  | ProfilePictureModalType
  | ReviewSessionModalType
  | SessionDetailsModalType
  | AdminUsersImportModalType
  | AdminManualUserImportModalType
  | NewMatchingModalType
  | AddToCalendarModalType
  | TermsModalType
  | AdminGroupStructureModalType
  | AdminReminderStructureModalType
  | ProgramCriteriasModalType
  | GroupSettingsModalType
  | ObjectiveDescriptionModalType
  | LinkSessionToObjectiveModalType
  | EditSessionModalType
  | NameMissingModalType
  | NoMentorFoundModalType
  | ContactWarningModalType
  | EditProfileModalType
  | MfaSetupModalType
  | MfaDisableModalType
  | MfaLoginModalType
  | ProgramObjectiveModalType
  | ImpersonationReasonModalType

export type ModalsStoreValuesType = {
  modals: Record<ModalStoreType['name'], ModalStoreType>
  shouldHideMatchingModals: boolean
}

export const INITIAL_MODALS_STORE_VALUES: ModalsStoreValuesType = {
  modals: {
    'phone': PhoneModalItem,
    'mentor-matching': MentorMatchingModalItem,
    'mentee-matching': MenteeMatchingModalItem,
    'no-program': NoProgramModalItem,
    'confirm-action': ConfirmActionModalItem,
    'new-session': newSessionModalItem,
    'review-session': reviewSessionModalItem,
    'meeting': meetingModalItem,
    'user-profile': userProfileModalItem,
    'edit-profile': editProfileModalItem,
    'profile-description': profileDescriptionModalItem,
    'profile-picture': profilePictureModalItem,
    'session-details': sessionDetailsModalItem,
    'new-matching': newMatchingModalItem,
    'admin-users-import': adminUsersImportModalItem,
    'admin-manual-user-import': adminManualUserImportModalItem,
    'add-to-calendar': addToCalendarModalItem,
    'terms': termsModalItem,
    'admin-group-structure': adminGroupStructureModalItem,
    'admin-reminder-structure': adminReminderStructureModalItem,
    'program-criterias': programCriteriasModalItem,
    'group-settings': groupSettingsModalItem,
    'objective-description': objectiveDescriptionModalItem,
    'link-session-to-objective': linkSessionToObjectiveModalItem,
    'edit-session': editSessionModalItem,
    'name-missing': nameMissingModalItem,
    'no-mentor-found': noMentorFoundModalItem,
    'contact-warning': contactWarningModalItem,
    'mfa-setup': mfaSetupModalItem,
    'mfa-disable': mfaDisableModalItem,
    'mfa-login': mfaLoginModalItem,
    'program-objective': programObjectiveModalItem,
    'impersonation-reason': impersonationReasonModalItem,
  },
  shouldHideMatchingModals: false,
}

const modalsStore = subscribeToStore(
  create(
    combine(INITIAL_MODALS_STORE_VALUES, (set, get) => ({
      toggleModal: (name: ModalStoreType['name'], isOpen: ModalStoreType['isOpen']) =>
        toggleModalAction(get, set, name, isOpen),
      updateModalAttachedData: <M extends ModalStoreType>(name: M['name'], attachedData: M['attachedData']) =>
        updateModalAttachedDatAction<M>(get, set, name, attachedData),
      setModalStep: <M extends ModalStoreType>(name: M['name'], step: M['steps'][number]) =>
        setModalStepAction<M>(get, set, name, step),
      toggleShouldHideMatchingModals: (isOpen: ModalsStoreValuesType['shouldHideMatchingModals']) =>
        toggleShouldHideMatchingModalsAction(get, set, isOpen),
    })),
  ),
)
export default modalsStore
